// ADR 6.9.26j §1 — the web joins the phones' auth vocabulary.
//
// Every name below is one `AnalyticsServiceAuth.kt` (or `AnalyticsService.swift`)
// already emits, with the properties those files already send. That is the
// whole decision: a `web_login` event would need every existing insight
// rewritten to union two names, while reusing `login` means the moment
// 6.9.26c's `$lib` breakdown lands, four dashboards count the web with no edit
// at all. AC5 reads both native files from disk and fails if either side
// renames.
//
// The one invented name is `web_signin_cost` (§4), and it is invented on
// purpose: the phones run the same two computations in `rust-core` at native
// speed, so pooling a browser's numbers with theirs would make the median
// meaningless.
//
// Every function here is a thin call to `capture()`, which is a no-op when
// PostHog was never constructed (6.9.26c §1) — so no call site in `auth/`
// branches on consent.

import type { IdentityUnavailableReason } from '../auth/google'
import { capture } from './capture'

/** Which door a session came through. The phones' `auth_method` vocabulary. */
export type AuthMethod = 'email' | 'google'

/**
 * Why a refresh failed, and web-only.
 *
 * Android's `tokenRefreshFailed()` captures **no properties at all**
 * (`AnalyticsServiceAuth.kt:73-74`), so its events land in an absent-`reason`
 * bucket rather than as a third value — which is the shape `auth-health.toml`'s
 * refresh-failure panel is written against.
 */
export type RefreshFailureReason = 'absent' | 'refused'

/** Registration begun — before the request, so the funnel has a first step. */
export function registrationStarted(): void {
  capture('registration_started', { auth_method: 'email' })
}

/**
 * Registration finished.
 *
 * `method` is a hard-coded `"email"` on both phones (`AnalyticsServiceAuth.kt:23`)
 * while `auth_method` carries the real one. The web reproduces the shape rather
 * than diverging from it; here the literal happens to be true, because web
 * registration is email-only.
 */
export function registrationCompleted(): void {
  capture('registration_completed', { method: 'email', auth_method: 'email' })
}

/** A sign-in through either door — never both, and never beside a registration. */
export function loginCompleted(method: AuthMethod): void {
  capture('login', { auth_method: method })
}

/** The GIS `social()` call was refused. Google is the web's only provider. */
export function socialLoginFailed(error: string): void {
  capture('social_login_failed', { provider: 'google', error })
}

/**
 * The Google button never rendered because the GIS script never became
 * available (ADR 15.9.26c §4). `reason` is `whenIdentityReady()`'s own,
 * passed through unchanged, so a dashboard row reads back to exactly one
 * branch of the wait.
 *
 * **The first name here the phones cannot emit** — there is no script to wait
 * for on either — so it is 6.9.26j AC5's second recorded asymmetry, in the
 * opposite direction from `auth_providers_fetch_failed`. `provider` is carried
 * for the same reason `social_login_failed` carries it: the auth-health
 * dashboard breaks that event down by provider.
 *
 * A **lower bound and a shape**, not a rate: the web's consent default is
 * denied (6.9.26c) and `/signin` is viewed signed-out, so the anonymous
 * cold-cache first visit fires this into a `capture()` with no PostHog behind
 * it. What is counted is a returning learner whose grant persisted.
 */
export function socialButtonUnavailable(reason: IdentityUnavailableReason): void {
  capture('social_button_unavailable', { provider: 'google', reason })
}

/**
 * A refresh was actually dispatched.
 *
 * Emitted only from the branch that reaches the transport — never from the
 * single-flight or latched early returns — so it is a usable denominator for
 * the failure rate rather than a count of callers.
 */
export function tokenRefreshTriggered(): void {
  capture('token_refresh_triggered', { source: 'auto_refresh' })
}

/**
 * The session ended without the user asking (§1).
 *
 * **Emitted from `refreshTokens()`' two failure branches, never from
 * `signOutLocally()`** — that function is also what a *voluntary* sign-out
 * calls, so emitting there would count every deliberate sign-out as a refresh
 * failure and corrupt the one insight this ADR exists to build.
 */
export function tokenRefreshFailed(reason: RefreshFailureReason): void {
  capture('token_refresh_failed', { reason })
}

/** What a sign-in attempt cost the browser that paid it (§4). */
export interface SignInCost {
  proving_ms: number
  hashing_ms: number
  difficulty: number
  auth_method: AuthMethod
  outcome: 'ok' | 'failed'
}

/**
 * One event per sign-in attempt — not one per stage, and **on both outcomes**.
 *
 * A draft emitted only on success, which drops exactly the population §4 exists
 * to find: the user on a slow device who pays five seconds of proof-of-work and
 * *then* gets refused. That is the worst experience this ladder produces and it
 * would have been the one experience invisible.
 */
export function signInCost(cost: SignInCost): void {
  capture('web_signin_cost', { ...cost })
}
